// Alerts, shared by the sidebar and Mission Control and polled once a minute.
// `open`: schedules the guard stopped after four failed runs in a row (urgent).
// `notices`: repositories where the code-map index found no agent instructions or
// no spec documents (guidance, kept out of the urgent count). `budgets`: a budget past its line, automated work
// paused under one, billing to review. `connectors`: a console connector whose tools changed. `hub`: the hub isn't taking
// new permissions (AgentCore's analysis ceiling, 2026-10-18). `maintenance`: a scheduled job that keeps Majlis working stopped
// succeeding (2026-10-26). Unknown kinds are ignored.
import { computed, ref } from "vue";
import { listAlerts, resolveAlert } from "@/api/console";
import type {
  Alert, BudgetAlert, BudgetReconciliationAlert, ConnectorDriftAlert, HubPolicyAlert, MaintenanceAlert, ScheduleAlert, SteeringNotice,
} from "@/api/types";
import { useApi } from "@/composables/useApi";

const alerts = ref<Alert[]>([]);

export const isScheduleAlert = (a: Alert): a is ScheduleAlert => (a.kind ?? "schedule_failing") === "schedule_failing";
export const isSteeringNotice = (a: Alert): a is SteeringNotice => a.kind === "repo_steering";
export const isBudgetAlert = (a: Alert): a is BudgetAlert | BudgetReconciliationAlert =>
  a.kind === "budget_threshold" || a.kind === "budget_paused" || a.kind === "budget_reconciliation";
export const isConnectorAlert = (a: Alert): a is ConnectorDriftAlert => a.kind === "connector_drift";
export const isHubAlert = (a: Alert): a is HubPolicyAlert => a.kind === "hub_policy";
export const isMaintenanceAlert = (a: Alert): a is MaintenanceAlert => a.kind === "maintenance";
let timer: ReturnType<typeof setInterval> | undefined;

export function useAlerts() {
  const { client } = useApi();
  async function refresh() {
    try { alerts.value = await listAlerts(client); } catch { /* the console works without alerts */ }
  }
  function start() {
    if (timer) return;
    void refresh();
    timer = setInterval(refresh, 60_000);
  }
  async function resolve(id: string, reEnable: boolean) {
    await resolveAlert(client, id, reEnable);
    await refresh();
  }
  const open = computed(() => alerts.value.filter((a): a is ScheduleAlert => isScheduleAlert(a) && a.status === "open"));
  const notices = computed(() => alerts.value.filter((a): a is SteeringNotice => isSteeringNotice(a) && a.status === "open"));
  const budgets = computed(() => alerts.value.filter((a): a is BudgetAlert | BudgetReconciliationAlert => isBudgetAlert(a) && a.status === "open"));
  const connectors = computed(() => alerts.value.filter((a): a is ConnectorDriftAlert => isConnectorAlert(a) && a.status === "open"));
  const hub = computed(() => alerts.value.filter((a): a is HubPolicyAlert => isHubAlert(a) && a.status === "open"));
  const maintenance = computed(() => alerts.value.filter((a): a is MaintenanceAlert => isMaintenanceAlert(a) && a.status === "open"));
  return { alerts, open, notices, budgets, connectors, hub, maintenance, start, refresh, resolve };
}

/** "f/jira_sprint_scorecard_active/daily_board_2424" → "daily_board_2424". */
export function scheduleName(path: string): string {
  return path.split("/").pop() ?? path;
}
